import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import moment from 'moment';
import Tw_Cen_MT_Negrita from '../../../../componentes/font/Tw_Cen_MT/TCB_____.TTF';
import Tw_Cen_MT from '../../../../componentes/font/Tw_Cen_MT/TCM_____.TTF';
import NumeroALetras from '../../../../componentes/funciones/NumeroALetras';
import { NegocioConfig } from "../../../../configuracion/Empresa";
import { formatoMoneda, formatoNumero } from "../../../../configuracion/validaciones";

Font.register({ family: 'Tw_Cen_MT', src: Tw_Cen_MT });
Font.register({ family: 'Tw_Cen_MT_Negrita', src: Tw_Cen_MT_Negrita });

const styles = StyleSheet.create({
    page: {
        fontFamily: 'Tw_Cen_MT',
        fontSize: 10,
        paddingTop: 40,
        paddingBottom: 50,
        paddingHorizontal: 60,
        backgroundColor: '#ffffff',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 5,
        paddingBottom: 0,
    },
    companyInfoLeft: {
        flexGrow: 1,
        textAlign: 'left',
    },
    companyName: {
        fontFamily: 'Tw_Cen_MT_Negrita',
        fontSize: 16,
        color: '#333',
        marginBottom: 3,
    },
    companyAddress: {
        fontSize: 9,
        color: '#666',
        marginBottom: 2,
    },
    logoContainer: {
        width: 100,
        height: 100,
        marginLeft: 20,
        marginTop: -10
    },
    logo: {
        width: 100,
        height: 100,
        objectFit: 'cover',
        alignSelf: 'center',
        marginBottom: 2,
        borderRadius: 15,
        overflow: 'hidden',
    },
    title: {
        fontSize: 16,
        fontFamily: 'Tw_Cen_MT_Negrita',
        color: '#222',
        letterSpacing: 1,
        textTransform: 'uppercase',
        marginTop: 15,
    },
    infoGrid: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 5,
        marginTop: 5,
    },
    infoColumn: {
        width: '33%',
    },
    infoLabel: {
        fontFamily: 'Tw_Cen_MT_Negrita',
        fontSize: 10,
        color: '#444',
        marginBottom: 3,
    },
    infoText: {
        fontSize: 10,
        marginBottom: 7,
        color: '#666',
    },
    table: {
        width: '100%',
        marginBottom: 10,
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#F5F5F5',
        borderBottomWidth: 1,
        borderBottomColor: '#E0E0E0',
        paddingVertical: 8,
        fontFamily: 'Tw_Cen_MT_Negrita',
        fontSize: 10,
        color: '#333',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#F0F0F0',
        paddingVertical: 6,
    },
    colDescription: { width: '45%', paddingLeft: 5 },
    colQty: { width: '15%', textAlign: 'center' },
    colPrice: { width: '20%', textAlign: 'right', paddingRight: 5 },
    colTotal: { width: '20%', textAlign: 'right', paddingRight: 5 },
    itemText: { fontSize: 10, color: '#555' },

    totalsWrapper: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginTop: 10,
    },
    totalsContainer: {
        width: '40%',
        borderTopWidth: 1.5,
        borderTopColor: '#E0E0E0',
        paddingTop: 10,
    },
    leftColumn: {
        textAlign: 'left',
        width: '50%',
        paddingTop: 5,
    },
    totalsRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 4,
    },
    totalLabel: { fontSize: 10, color: '#555' },
    totalValue: { fontSize: 10, fontFamily: 'Tw_Cen_MT_Negrita', color: '#333' },

    grandTotalRow: {
        marginTop: 8,
        paddingTop: 8,
        borderTopWidth: 1,
        borderTopColor: '#D0D0D0',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    grandTotalLabel: { fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 13, color: '#000' },
    grandTotalValue: { fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 13, color: '#000' },

    amountInWords: {
        fontSize: 9,
        fontFamily: 'Tw_Cen_MT_Negrita',
        textTransform: 'uppercase',
        marginBottom: 5,
    },
    paymentDetails: {
        marginTop: 10,
        paddingTop: 10,
        borderTopWidth: 1,
        borderTopColor: '#F0F0F0',
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: 8,
        color: '#888',
        paddingHorizontal: 60,
    },
});

const ReporteCompras = ({ compra }) => {
    if (!compra) {
        return <Document><Page size="LETTER" style={styles.page}><Text>No hay datos de compra para mostrar.</Text></Page></Document>;
    }

    const {
        numeroFactura, proveedor, Empleado, compraDetalles = [],
        totalCompra = 0, pagoCompras: pagocompras = [],
    } = compra;

    const pagosCompletados = pagocompras?.map(pc => pc.pago).filter(p => p && p.estado !== 'Anulada') || [];
    const pago = pagosCompletados[pagosCompletados.length - 1] || {};
    const recibido = parseFloat(pago.recibido || 0);
    const cambio = Math.max(recibido - parseFloat(totalCompra), 0);
    const efectivoPagado = pago.pagoefectivos?.reduce((sum, p) => sum + parseFloat(p.monto || 0), 0) || 0;
    const transferenciaPagado = pago.pagotransferencia?.reduce((sum, p) => sum + parseFloat(p.monto || 0), 0) || 0;
    const tarjetaPagado = pago.pagotarjeta?.reduce((sum, p) => sum + parseFloat(p.monto || 0), 0) || 0;

    const subtotalBruto = compraDetalles.reduce((total, item) => {
        const cantidad = parseFloat(item.cantidad || 0);
        const costo = parseFloat(item.costo || 0);
        return total + (cantidad * costo);
    }, 0);

    const numeroDocumento = formatoNumero(numeroFactura) || String(compra?.id || "—");

    return (
        <Document>
            <Page size="LETTER" style={styles.page}>
                <View style={styles.header}>
                    <View style={styles.companyInfoLeft}>
                        <Text style={styles.companyName}>{NegocioConfig.nombre}</Text>
                        <Text style={styles.companyAddress}>{NegocioConfig.direccion}</Text>
                        <Text style={styles.companyAddress}>RTN: {NegocioConfig.rtn} Email: {NegocioConfig.correo}</Text>
                        <Text style={styles.companyAddress}>Tel: {NegocioConfig.telefono}</Text>
                        <Text style={styles.title}>COMPROBANTE DE COMPRA N° {numeroDocumento}</Text>
                    </View>
                    {NegocioConfig.logo && (
                        <View style={styles.logoContainer}>
                            <Image style={styles.logo} src={NegocioConfig.logo} />
                        </View>
                    )}
                </View>

                <View style={styles.infoGrid}>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>Fecha de Compra:</Text>
                        <Text style={styles.infoText}>{moment(compra?.fechaCreacion).format('DD/MM/YYYY HH:mm')}</Text>
                    </View>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>Registrado Por:</Text>
                        <Text style={styles.infoText}>{Empleado?.nombreCorto || '—'}</Text>
                    </View>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>Estado:</Text>
                        <Text style={styles.infoText}>{compra.estado || 'Completado'}</Text>
                    </View>
                </View>

                <View style={styles.infoGrid}>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>Proveedor:</Text>
                        <Text style={styles.infoText}>{proveedor?.nombre || 'N/A'}</Text>
                    </View>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>RTN Proveedor:</Text>
                        <Text style={styles.infoText}>{proveedor?.rtn || 'N/A'}</Text>
                    </View>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>N° Factura Original:</Text>
                        <Text style={styles.infoText}>{compra?.numeroFactura || 'N/A'}</Text>
                    </View>
                </View>

                <View style={styles.table}>
                    <View style={styles.tableHeader}>
                        <Text style={styles.colDescription}>DESCRIPCIÓN</Text>
                        <Text style={styles.colQty}>CANT.</Text>
                        <Text style={styles.colPrice}>COSTO UNIT.</Text>
                        <Text style={styles.colTotal}>SUBTOTAL</Text>
                    </View>
                    {compraDetalles.map((item, index) => (
                        <View key={index} style={styles.tableRow}>
                            <View style={styles.colDescription}>
                                <Text style={styles.itemText}>{item.producto?.nombre || 'N/A'}</Text>
                            </View>
                            <Text style={[styles.itemText, styles.colQty]}>{parseFloat(item.cantidad).toFixed(2)}</Text>
                            <Text style={[styles.itemText, styles.colPrice]}>{formatoMoneda(item.costo)}</Text>
                            <Text style={[styles.itemText, styles.colTotal]}>{formatoMoneda(item.cantidad * item.costo)}</Text>
                        </View>
                    ))}
                </View>

                <View style={styles.totalsWrapper}>
                    <View style={styles.leftColumn}>
                        <Text style={styles.infoLabel}>VALOR EN LETRAS:</Text>
                        <Text style={styles.amountInWords}>
                            {NumeroALetras(totalCompra)}
                        </Text>

                        {pagosCompletados.length > 0 && (
                            <View style={styles.paymentDetails}>
                                <Text style={[styles.infoLabel, { marginBottom: 5 }]}>DETALLE DE PAGO</Text>
                                <View style={styles.totalsRow}>
                                    <Text style={styles.totalLabel}>Total Pagado:</Text>
                                    <Text style={styles.totalValue}>{formatoMoneda(recibido)}</Text>
                                </View>
                                {efectivoPagado > 0 &&
                                    <View style={styles.totalsRow}>
                                        <Text style={styles.totalLabel}>Efectivo:</Text>
                                        <Text style={styles.totalValue}>{formatoMoneda(efectivoPagado)}</Text>
                                    </View>
                                }
                                {tarjetaPagado > 0 &&
                                    <View style={styles.totalsRow}>
                                        <Text style={styles.totalLabel}>Tarjeta:</Text>
                                        <Text style={styles.totalValue}>{formatoMoneda(tarjetaPagado)}</Text>
                                    </View>
                                }
                                {transferenciaPagado > 0 &&
                                    <View style={styles.totalsRow}>
                                        <Text style={styles.totalLabel}>Transferencia:</Text>
                                        <Text style={styles.totalValue}>{formatoMoneda(transferenciaPagado)}</Text>
                                    </View>
                                }
                                <View style={[styles.totalsRow, { marginTop: 3 }]}>
                                    <Text style={styles.totalLabel}>Cambio:</Text>
                                    <Text style={styles.totalValue}>{formatoMoneda(cambio)}</Text>
                                </View>
                            </View>
                        )}
                    </View>

                    <View style={styles.totalsContainer}>
                        <View style={styles.totalsRow}>
                            <Text style={styles.totalLabel}>Subtotal Ítems:</Text>
                            <Text style={styles.totalValue}>{formatoMoneda(subtotalBruto)}</Text>
                        </View>
                        <View style={styles.grandTotalRow}>
                            <Text style={styles.grandTotalLabel}>TOTAL COMPRA:</Text>
                            <Text style={styles.grandTotalValue}>{formatoMoneda(totalCompra)}</Text>
                        </View>
                        <Text style={styles.amountInWords}>{NumeroALetras(totalCompra)}</Text>
                    </View>
                </View>

                <View style={styles.footer} fixed>
                    <Text style={{ fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 10, marginBottom: 5 }}>
                        Comprobante de compra generado internamente.
                    </Text>
                    <View style={styles.infoGrid}>
                        <View style={styles.infoColumn}>
                            <Text style={styles.infoLabel}>Copia: Proveedor</Text>
                        </View>
                        <View style={styles.infoColumn}>
                            <Text style={styles.infoLabel}>Original: Archivo</Text>
                        </View>
                    </View>
                </View>
            </Page>
        </Document>
    );
};

export default ReporteCompras;